import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query'

import {
  type VideoItem,
  type VideoOrientation,
  VIDEOS_DATA,
  type VideoSource,
} from '#/lib/videos'
import {STALE} from '#/state/queries'

/**
 * Query parameters for filtering and sorting videos
 */
export interface VideosQueryParams {
  source?: VideoSource | 'all'
  orientation?: VideoOrientation | 'all'
  search?: string
  sort?: 'newest' | 'oldest' | 'title'
}

/**
 * Default query parameters
 */
export const DEFAULT_VIDEOS_PARAMS: VideosQueryParams = {
  source: 'all',
  orientation: 'all',
  sort: 'newest',
}

/**
 * Number of videos to fetch per page
 */
const PAGE_SIZE = 33

/**
 * Response shape for video fetching (API-ready)
 */
export interface VideosPage {
  videos: VideoItem[]
  cursor?: string
}

/**
 * Query key root for videos queries
 */
export const RQKEY_ROOT = 'videos'

/**
 * Generate query key for videos query
 */
export function RQKEY(params?: VideosQueryParams): QueryKey {
  return [RQKEY_ROOT, params || DEFAULT_VIDEOS_PARAMS]
}

/**
 * Mock fetch function that simulates API pagination
 * This will be replaced with actual API calls in the future
 */
async function fetchVideosMock(params: {
  cursor?: string
  limit: number
  filters: VideosQueryParams
}): Promise<VideosPage> {
  const {cursor, limit, filters} = params

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 300))

  // Filter videos based on params
  let filteredVideos = [...VIDEOS_DATA]

  // Filter by source
  if (filters.source && filters.source !== 'all') {
    filteredVideos = filteredVideos.filter(v => v.source === filters.source)
  }

  // Filter by orientation
  if (filters.orientation && filters.orientation !== 'all') {
    filteredVideos = filteredVideos.filter(
      v => v.orientation === filters.orientation,
    )
  }

  // Filter by search term (case-insensitive title search)
  if (filters.search && filters.search.trim()) {
    const searchLower = filters.search.toLowerCase().trim()
    filteredVideos = filteredVideos.filter(v =>
      v.title.toLowerCase().includes(searchLower),
    )
  }

  // Sort videos
  if (filters.sort === 'title') {
    filteredVideos.sort((a, b) => a.title.localeCompare(b.title))
  } else if (filters.sort === 'oldest') {
    // For mock data, reverse the array to simulate oldest first
    filteredVideos.reverse()
  }
  // 'newest' is the default order (as stored)

  // Calculate pagination
  const startIndex = cursor ? parseInt(cursor, 10) : 0
  const endIndex = startIndex + limit
  const paginatedVideos = filteredVideos.slice(startIndex, endIndex)

  // Determine if there are more results
  const hasMore = endIndex < filteredVideos.length
  const nextCursor = hasMore ? String(endIndex) : undefined

  return {
    videos: paginatedVideos,
    cursor: nextCursor,
  }
}

/**
 * Hook for fetching videos with infinite scroll support
 *
 * @param params - Optional query parameters for filtering/sorting
 * @returns TanStack Query infinite query result
 *
 * @example
 * ```tsx
 * const {data, fetchNextPage, hasNextPage, isFetchingNextPage} = useVideosQuery({
 *   source: 'youtube',
 *   orientation: 'horizontal',
 * })
 *
 * const videos = data?.pages.flatMap(page => page.videos) ?? []
 * ```
 */
export function useVideosQuery(params?: VideosQueryParams) {
  const mergedParams = {...DEFAULT_VIDEOS_PARAMS, ...params}

  return useInfiniteQuery<
    VideosPage,
    Error,
    InfiniteData<VideosPage>,
    QueryKey,
    string | undefined
  >({
    queryKey: RQKEY(mergedParams),
    queryFn: async ({pageParam}) => {
      return fetchVideosMock({
        cursor: pageParam,
        limit: PAGE_SIZE,
        filters: mergedParams,
      })
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => lastPage.cursor,
    staleTime: STALE.MINUTES.FIVE,
  })
}

/**
 * Helper to flatten paginated video data
 */
export function flattenVideosPages(
  data: InfiniteData<VideosPage> | undefined,
): VideoItem[] {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.videos)
}
